/*
 * ctf-product-gallery.js
 * Galería de PDP — componente shared (ctf-product + ctf-personalised-product).
 *
 * Features:
 *   - Crossfade real con dos <img> superpuestos + preload con decode()
 *     (sin blink/flash entre cambios).
 *   - Auto-rotate cada AUTO_INTERVAL_MS. Pausa en hover (desktop),
 *     en visibilitychange (tab oculta) y en prefers-reduced-motion.
 *   - Click en thumb cambia + pausa RESUME_AFTER_INTERACT.
 *   - Swipe horizontal en mobile/tablet via pointer events:
 *       - Mientras se arrastra, la imagen sigue al dedo con resistance.
 *       - Si el delta supera el threshold (o velocity), se confirma el cambio
 *         y la imagen siguiente entra desde el lado correcto.
 *       - Si no, vuelve a posición original.
 *       - Distingue swipe horizontal de scroll vertical (ratio dx/dy).
 *   - MutationObserver re-inicia en section-reload AJAX de Shopify.
 *
 * Sin handlers inline. Sin globals.
 */
(function () {
  'use strict';

  // ── Config ────────────────────────────────────────────────────
  var AUTO_INTERVAL_MS      = 9000;
  var RESUME_AFTER_INTERACT = 5000;
  var ANIM_MS               = 400;       // matchea CSS transition
  var SWIPE_MIN_DX          = 50;        // px mínimo para considerar swipe
  var SWIPE_VELOCITY        = 0.4;       // px/ms — si supera esto, swipe aunque dx sea bajo
  var SWIPE_MAX_DY_RATIO    = 0.6;       // si |dy| > |dx| * ratio, se considera scroll vertical
  var DRAG_RESISTANCE       = 0.55;      // 0..1: cuánto sigue el dedo (1 = exacto)

  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── Utils ─────────────────────────────────────────────────────
  function decode(img) {
    if (img && typeof img.decode === 'function') {
      return img.decode().catch(function () {});
    }
    return new Promise(function (resolve) {
      if (!img || img.complete) return resolve();
      img.addEventListener('load',  resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  // ── Zoom controller unificado (mouse + touch) ─────────────────
  // Overlay con una sola imagen a la vez que se transforma (scale + translate).
  //   Desktop: rueda hacia el cursor + click alterna 1x↔2x + arrastre paneo.
  //   Mobile:  pinch + doble-tap alterna + arrastre paneo.
  // Abre siempre con la imagen encajada (fit) en pantalla.
  function createZoom(dialog) {
    var stage   = dialog.querySelector('[data-ctf-zoom-stage]');
    var img     = dialog.querySelector('[data-ctf-zoom-img]');
    var thumbs  = Array.prototype.slice.call(dialog.querySelectorAll('[data-ctf-zoom-thumb]'));
    var closeBtn = dialog.querySelector('[data-ctf-zoom-close]');
    var prevBtn  = dialog.querySelector('[data-ctf-zoom-prev]');
    var nextBtn  = dialog.querySelector('[data-ctf-zoom-next]');
    if (!stage || !img) return null;

    var MIN = 1, MAX = 4, TOGGLE = 2;
    var scale = 1, tx = 0, ty = 0, index = 0;
    var baseW = 0, baseH = 0;            // tamaño "fit" (scale 1)
    var pointers = new Map();
    var pinchDist = 0, pinchScale = 1;
    var dragging = false, moved = false, lastX = 0, lastY = 0, downX = 0, downY = 0;
    var lastTap = 0;

    function count() { return thumbs.length || 1; }
    function srcFor(i) { return thumbs[i] ? thumbs[i].getAttribute('data-full') : img.getAttribute('data-full'); }
    function altFor(i) { return thumbs[i] ? (thumbs[i].getAttribute('data-alt') || '') : (img.getAttribute('alt') || ''); }

    function setActiveThumb(i) {
      thumbs.forEach(function (t, j) { t.setAttribute('aria-selected', j === i ? 'true' : 'false'); });
    }

    function computeBase() {
      var r = stage.getBoundingClientRect();
      var nw = img.naturalWidth || 1, nh = img.naturalHeight || 1;
      var ar = nw / nh;
      if (r.width / r.height > ar) { baseH = r.height; baseW = r.height * ar; }
      else { baseW = r.width; baseH = r.width / ar; }
    }

    function clampT() {
      var r = stage.getBoundingClientRect();
      var maxX = Math.max(0, (baseW * scale - r.width) / 2);
      var maxY = Math.max(0, (baseH * scale - r.height) / 2);
      tx = Math.max(-maxX, Math.min(maxX, tx));
      ty = Math.max(-maxY, Math.min(maxY, ty));
    }

    function apply() {
      clampT();
      img.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
      dialog.classList.toggle('is-zoomed', scale > 1.001);
    }

    function reset() { scale = 1; tx = 0; ty = 0; apply(); }

    function zoomToPoint(newScale, clientX, clientY) {
      newScale = Math.max(MIN, Math.min(MAX, newScale));
      var r = stage.getBoundingClientRect();
      var cx = clientX - (r.left + r.width / 2);
      var cy = clientY - (r.top + r.height / 2);
      var factor = newScale / scale;
      tx = cx - (cx - tx) * factor;
      ty = cy - (cy - ty) * factor;
      scale = newScale;
      if (scale <= MIN) { scale = MIN; tx = 0; ty = 0; }
      apply();
    }

    function updateNav() {
      var multi = count() > 1;
      if (prevBtn) prevBtn.style.display = multi ? '' : 'none';
      if (nextBtn) nextBtn.style.display = multi ? '' : 'none';
    }

    function load(i) {
      index = ((i % count()) + count()) % count();
      var s = srcFor(index);
      img.alt = altFor(index);
      setActiveThumb(index);
      var onReady = function () { computeBase(); reset(); };
      img.onload = onReady;
      if (s && img.getAttribute('src') !== s) { img.src = s; }
      else if (img.complete && img.naturalWidth) { onReady(); }
    }

    function open(i) {
      load(i || 0);
      updateNav();
      if (!dialog.open) dialog.showModal();
    }
    function close() { if (dialog.open) dialog.close(); }

    function dist(a, b) { var dx = a.x - b.x, dy = a.y - b.y; return Math.sqrt(dx * dx + dy * dy); }

    // Rueda (desktop): zoom hacia el cursor.
    stage.addEventListener('wheel', function (e) {
      e.preventDefault();
      var f = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      zoomToPoint(scale * f, e.clientX, e.clientY);
    }, { passive: false });

    stage.addEventListener('pointerdown', function (e) {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try { stage.setPointerCapture(e.pointerId); } catch (_) {}
      if (pointers.size === 2) {
        var p = Array.from(pointers.values());
        pinchDist = dist(p[0], p[1]);
        pinchScale = scale;
      } else {
        dragging = true; moved = false;
        lastX = downX = e.clientX; lastY = downY = e.clientY;
      }
    });

    stage.addEventListener('pointermove', function (e) {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size >= 2) {
        var p = Array.from(pointers.values());
        var d = dist(p[0], p[1]);
        if (pinchDist > 0) {
          var mx = (p[0].x + p[1].x) / 2, my = (p[0].y + p[1].y) / 2;
          zoomToPoint(pinchScale * (d / pinchDist), mx, my);
        }
        return;
      }
      if (dragging) {
        var dx = e.clientX - lastX, dy = e.clientY - lastY;
        if (Math.abs(e.clientX - downX) > 6 || Math.abs(e.clientY - downY) > 6) moved = true;
        if (scale > 1.001) { tx += dx; ty += dy; apply(); }
        lastX = e.clientX; lastY = e.clientY;
      }
    });

    function endPointer(e) {
      var was2 = pointers.size >= 2;
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinchDist = 0;
      try { stage.releasePointerCapture(e.pointerId); } catch (_) {}
      if (was2) return;
      if (dragging) {
        dragging = false;
        if (!moved) handleTap(e);
      }
    }
    stage.addEventListener('pointerup', endPointer);
    stage.addEventListener('pointercancel', function (e) {
      pointers.delete(e.pointerId); dragging = false; pinchDist = 0;
    });

    // Tap/click: sobre la imagen alterna zoom; fuera de la imagen cierra.
    function handleTap(e) {
      var onImg = e.target === img;
      var now = Date.now();
      lastTap = now;
      if (!onImg) { close(); return; }
      if (scale > 1.001) zoomToPoint(MIN, e.clientX, e.clientY);
      else zoomToPoint(TOGGLE, e.clientX, e.clientY);
    }

    if (closeBtn) closeBtn.addEventListener('click', function (e) { e.preventDefault(); close(); });
    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.preventDefault(); load(index - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.preventDefault(); load(index + 1); });
    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function (e) { e.preventDefault(); load(i); });
    });

    dialog.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); load(index - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); load(index + 1); }
    });

    // Reset al cerrar (Esc nativo, botón, o tap fuera).
    dialog.addEventListener('close', function () { img.onload = null; reset(); });

    window.addEventListener('resize', function () {
      if (dialog.open) { computeBase(); apply(); }
    });

    return { open: open, close: close };
  }

  // ── Per-gallery init ──────────────────────────────────────────
  function initGallery(root) {
    if (!root || root.dataset.galleryInit === '1') return;
    root.dataset.galleryInit = '1';

    var mainBox = root.querySelector('.ctf-product-gallery__main');
    var imgs    = root.querySelectorAll('.ctf-product-gallery__main-img');
    var thumbs  = Array.prototype.slice.call(
      root.querySelectorAll('.ctf-product-gallery__thumb')
    );
    if (!mainBox || imgs.length < 2 || thumbs.length <= 1) return;

    var current = imgs[0];
    var next    = imgs[1];
    var idx       = 0;
    var timer     = null;
    var animating = false;

    // Zoom: controlador unificado propio (mouse + touch). Abre el overlay
    // en el índice actualmente visible (alineado 1:1 con product.images).
    var zoomDialog = root.querySelector('[data-ctf-zoom]');
    var zoomCtl = zoomDialog ? createZoom(zoomDialog) : null;
    function openZoom() {
      if (zoomCtl) zoomCtl.open(idx);
    }

    function setActiveThumb(i) {
      thumbs.forEach(function (t, j) {
        var on = j === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    // Cambia a la imagen targetIdx con crossfade (con dirección opcional)
    function go(targetIdx, opts) {
      opts = opts || {};
      if (animating) return;
      var n = thumbs.length;
      targetIdx = ((targetIdx % n) + n) % n;
      if (targetIdx === idx) return;

      var src = thumbs[targetIdx].getAttribute('data-src');
      if (!src) return;

      animating = true;
      next.src = src;

      decode(next).then(function () {
        // Crossfade vía swap de clases — current pasa a estar oculto,
        // next pasa a current. Las referencias se swap-ean después.
        next.classList.remove('is-next');
        next.classList.add('is-current');
        current.classList.remove('is-current');
        current.classList.add('is-next');

        // Cleanup tras la transición
        setTimeout(function () {
          // Asegura que el ahora-"next" queda en posición base
          current.style.transform = '';
          next.style.transform = '';
          // Swap references
          var tmp = current;
          current = next;
          next = tmp;
          animating = false;
        }, prefersReducedMotion ? 10 : ANIM_MS + 20);
      });

      idx = targetIdx;
      setActiveThumb(idx);
    }

    // ── Auto-rotate ────────────────────────────────────────────
    // Desactivado: la galería ya no rota sola entre imagen principal y secundaria.
    // Se mantiene swipe manual, click en thumb y zoom intactos.
    function startAuto() { /* no-op */ }
    function stopAuto()  { if (timer) { clearInterval(timer); timer = null; } }
    function pauseThenResume() { /* no-op — no hay auto que resumir */ }

    // ── Thumb click ────────────────────────────────────────────
    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function (ev) {
        ev.preventDefault();
        go(i);
        pauseThenResume();
      });
    });

    // ── Pause on hover (desktop) ───────────────────────────────
    root.addEventListener('mouseenter', stopAuto);
    root.addEventListener('mouseleave', startAuto);

    // ── Pause when tab hidden ──────────────────────────────────
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopAuto(); else startAuto();
    });

    // ── Swipe / Drag (touch + pen + mouse) ─────────────────────
    var ptr = null;
    var dragging = false;

    function onPointerDown(e) {
      // Solo touch/pen — mouse drag se interpreta solo si está sobre la imagen
      // (los thumbs ya tienen su click handler arriba).
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      ptr = {
        x: e.clientX, y: e.clientY, t: Date.now(),
        id: e.pointerId, type: e.pointerType,
        decided: false
      };
      dragging = false;
      stopAuto();
    }

    function onPointerMove(e) {
      if (!ptr || e.pointerId !== ptr.id) return;
      var dx = e.clientX - ptr.x;
      var dy = e.clientY - ptr.y;

      // Decide en el primer movimiento significativo si es swipe horizontal
      // o scroll vertical. Solo capturamos si parece horizontal.
      if (!ptr.decided) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dy) > Math.abs(dx) * SWIPE_MAX_DY_RATIO) {
          // Scroll vertical → abandonamos
          ptr = null;
          return;
        }
        ptr.decided = true;
        dragging = true;
        mainBox.classList.add('is-dragging');
        try { mainBox.setPointerCapture(e.pointerId); } catch (_) {}
      }

      // Feedback visual: mover la imagen actual con el dedo (con resistance)
      var shift = dx * DRAG_RESISTANCE;
      current.style.transition = 'none';
      current.style.transform = 'translateX(' + shift + 'px)';
      // Preload del candidato (siguiente o anterior) para que el commit sea instantáneo
      var candidate = dx < 0 ? (idx + 1) : (idx - 1);
      var cn = ((candidate % thumbs.length) + thumbs.length) % thumbs.length;
      var candidateSrc = thumbs[cn].getAttribute('data-src');
      if (candidateSrc && next.getAttribute('src') !== candidateSrc) {
        next.src = candidateSrc;
      }
      e.preventDefault && e.preventDefault();
    }

    function onPointerEnd(e) {
      if (!ptr) return;
      var dx = e.clientX - ptr.x;
      var dy = e.clientY - ptr.y;
      var dt = Math.max(1, Date.now() - ptr.t);
      var velocity = Math.abs(dx) / dt; // px/ms
      var wasDragging = dragging;

      mainBox.classList.remove('is-dragging');
      try { mainBox.releasePointerCapture && mainBox.releasePointerCapture(ptr.id); } catch (_) {}
      ptr = null;
      dragging = false;

      // Reset transition para que vuelva animado
      current.style.transition = '';

      if (!wasDragging) {
        // No fue swipe — fue un tap/click sobre la imagen → abrir zoom.
        openZoom();
        setTimeout(startAuto, RESUME_AFTER_INTERACT);
        return;
      }

      var horizontal = Math.abs(dx) > Math.abs(dy) * 0.8;
      var pastThreshold = Math.abs(dx) >= SWIPE_MIN_DX || velocity >= SWIPE_VELOCITY;

      if (horizontal && pastThreshold) {
        // Confirma cambio
        current.style.transform = ''; // animará de vuelta a 0 al hacer el swap
        if (dx < 0) go(idx + 1);
        else        go(idx - 1);
      } else {
        // Cancela: vuelve a posición original
        current.style.transform = 'translateX(0)';
      }
      pauseThenResume();
    }

    mainBox.addEventListener('pointerdown', onPointerDown);
    mainBox.addEventListener('pointermove', onPointerMove);
    mainBox.addEventListener('pointerup', onPointerEnd);

    // Accesibilidad: Enter / Espacio sobre la imagen principal abren el zoom.
    mainBox.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        openZoom();
      }
    });
    mainBox.addEventListener('pointercancel', function (e) {
      if (!ptr) return;
      mainBox.classList.remove('is-dragging');
      current.style.transition = '';
      current.style.transform = 'translateX(0)';
      ptr = null;
      dragging = false;
      pauseThenResume();
    });

    // Inicio
    startAuto();
  }

  function initAll(scope) {
    var galleries = (scope || document).querySelectorAll('.ctf-product-gallery');
    Array.prototype.forEach.call(galleries, initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Re-init si Shopify re-renderiza el PDP via AJAX section reload
  var pending = false;
  var mo = new MutationObserver(function (muts) {
    if (pending) return;
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      if (!added || !added.length) continue;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if ((n.classList && n.classList.contains('ctf-product-gallery')) ||
            (n.querySelector && n.querySelector('.ctf-product-gallery'))) {
          pending = true;
          requestAnimationFrame(function () { pending = false; initAll(); });
          return;
        }
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();
